import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock, Mail, GraduationCap, Shield, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("student");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== role) setError(`Signed in as ${u.role}. Redirecting…`);
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-[#0F172A] grid-bg p-12 flex-col justify-between overflow-hidden">
        <Logo light />
        <div className="relative z-10 fade-up">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-blue-300 mb-5">Practice · Time · Improve</div>
          <h1 className="font-heading text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
            Sit the exam<br />before the exam.
          </h1>
          <p className="text-slate-400 mt-6 max-w-md text-base leading-relaxed">
            Timed mock tests with instant scoring and a full answer review — so you walk into the real thing already prepared.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 max-w-md">
          {["Timed", "Auto-graded", "Reviewed"].map((t, i) => (
            <div key={t} className="fade-up border border-white/10 rounded-lg px-4 py-3 text-sm text-slate-300" style={{ animationDelay: `${150 + i * 100}ms` }}>
              <span className="font-mono text-blue-400 mr-1.5">0{i + 1}</span>{t}
            </div>
          ))}
        </div>
        <img src="https://images.pexels.com/photos/6345317/pexels-photo-6345317.jpeg?auto=compress&w=900" alt=""
          className="absolute right-0 bottom-0 w-2/3 h-2/3 object-cover opacity-[0.12] pointer-events-none" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-10"><Logo /></div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Sign in</h2>
          <p className="text-slate-500 mt-2 text-sm">Use the account provided by your institute.</p>

          <Tabs value={role} onValueChange={setRole} className="mt-8">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="student" data-testid="login-role-tab-student"><GraduationCap className="h-4 w-4 mr-1.5" />Student</TabsTrigger>
              <TabsTrigger value="admin" data-testid="login-role-tab-admin"><Shield className="h-4 w-4 mr-1.5" />Admin</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 bg-white" placeholder="you@example.com" data-testid="login-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11 bg-white" placeholder="••••••••" data-testid="login-password-input" />
              </div>
            </div>
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white text-white font-medium transition-colors">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Sign in as ${role === "admin" ? "Admin" : "Student"}`}
            </Button>
            <div className="text-right">
              <ForgotPasswordDialog trigger={
                <button type="button" className="text-xs text-[#1D4ED8] hover:underline inline-flex items-center gap-1" data-testid="login-forgot-password">
                  <LifeBuoy className="h-3 w-3" />Forgot password?
                </button>
              } />
            </div>
          </form>
          <p className="text-xs text-slate-400 mt-8">
            Don't have an account?{" "}
            <Link to="/signup" className="font-medium text-[#1D4ED8] hover:underline" data-testid="login-signup-link">Create one</Link>
            {" "}· Pinslib library members, sign in with your pinslib.in credentials.
          </p>
        </div>
      </div>
    </div>
  );
}
